import { SessionStorageKeys } from '@aglenergy/shared-library';
import { store } from 'app/shared/store/store';
import { bundleApiService } from 'app/bundle/services/bundle-api.service';
import { AddressDetailModel } from 'app/shared/models/address/address.model';
import { BundleSearchResponse } from 'app/shared/models/bundle-search-response.model';
import { BundleSearchStore, CurrentSearchRequestModel, EnergySearchStore } from 'app/shared/models/current-search-request.model';
import { CouponCode } from 'app/shared/models/couponCode.model';
import { CustomerType, ProductSearchRequestModel } from 'app/shared/models/productSearchRequestModel';
import { EnergyProductsResponse } from 'app/shared/models/product-response.model';
import { FuelType, SearchRequestType } from 'app/shared/models/energy/energy.enums';
import { ProductCategory } from 'app/shared/models/product-category.model';
import { StoreKeys } from 'app/shared/constants/store-keys.enum';
import { addressService } from 'app/shared/services/address/address.service';
import { energyProductSearchService } from 'app/energy/services/energy-product-search.service';
import { energyRequestMapper } from 'app/energy/mappers/energy-request.mapper';
import { isSameAddress } from 'app/shared/utilities/address.util';
import { nbnProductService } from 'app/nbn/services/nbn-product/nbn-product.service';
import { productsSessionStorageService } from 'app/shared/services/product-session-storage/product-session-storage.service';

type FirstAvailableProductCategory = ProductCategory | 'none';

class BundleSearchService {
  private getBundleRequestModel(address: AddressDetailModel): CurrentSearchRequestModel {
    const productCategories: ProductCategory[] = ['energy', 'nbn', 'mobile plan'];

    const searchRequest = energyRequestMapper.mapToCurrentSearchRequestModel(
      address,
      CustomerType.residential,
      productCategories,
      SearchRequestType.address
    );

    return searchRequest;
  }

  public getProducts(
    address?: AddressDetailModel,
    currentSearchRequest?: CurrentSearchRequestModel,
    couponCode?: string,
    couponCodes?: CouponCode[]
  ): Promise<BundleSearchResponse> {
    let searchRequest: CurrentSearchRequestModel;
    if (address) {
      searchRequest = this.getBundleRequestModel(address);
    } else {
      searchRequest = currentSearchRequest;
    }

    searchRequest.productSearchRequestModel = { ...searchRequest.productSearchRequestModel, shouldReturnDiscounts: true };
    // check the fuel type and default to dual if could not find.
    if (!searchRequest.productSearchRequestModel?.fuelType) {
      searchRequest.productSearchRequestModel = { ...searchRequest.productSearchRequestModel, fuelType: FuelType.Dual };
    }

    if (couponCode) {
      searchRequest.productSearchRequestModel = { ...searchRequest.productSearchRequestModel, couponCode };
    }

    if (couponCodes) {
      //TODO: Update shared library model
      searchRequest.productSearchRequestModel = { ...searchRequest.productSearchRequestModel, couponCodes } as any;
    }

    // Invoke Bundle search
    return bundleApiService.getBundleProducts(searchRequest.productSearchRequestModel).then((searchResponse) => {
      searchRequest.productSearchRequestModel.nbnProductLoaded = true;

      // Handle Energy Search Response
      if (searchResponse?.energyProductsResponse !== null) {
        searchResponse.energyProductsResponse = Object.assign(searchResponse.energyProductsResponse, {
          isTariffUpdate: false,
          isRatesPeriodToggleUpdate: false
        });

        energyProductSearchService.saveProductsInStore(searchRequest, searchResponse?.energyProductsResponse);
      }

      // Handle Nbn Search Response
      if (searchResponse?.nbnProductsResponse !== null) {
        if (nbnProductService.hasNoService(searchResponse.nbnProductsResponse)) {
          searchResponse.nbnProductsResponse = null;
        } else {
          productsSessionStorageService.upsertProducts(searchResponse?.nbnProductsResponse, 'nbn', address);
        }
      }
      sessionStorage.setItem(SessionStorageKeys.address, JSON.stringify(address));
      bundleSearchService.saveProductsInStore(address, searchResponse);
      return searchResponse;
    });
  }

  public getAuthenticatedProducts(): Promise<BundleSearchResponse> {
    // Invoke Bundle search
    return bundleApiService.getAuthenticatedBundleProducts().then((searchResponse) => {
      const address = {
        postcode: searchResponse?.contractAddress?.postcode,
        state: searchResponse?.contractAddress?.state,
        streetNumber: searchResponse?.contractAddress?.streetNumber,
        streetName: searchResponse?.contractAddress?.streetName,
        streetType: searchResponse?.contractAddress?.streetType,
        suburb: searchResponse?.contractAddress?.suburb,
        unitNumber: searchResponse?.contractAddress?.unitNumber
      };
      // Handle Nbn Search Response
      if (searchResponse?.nbnProductsResponse !== null) {
        if (nbnProductService.hasNoService(searchResponse.nbnProductsResponse)) {
          searchResponse.nbnProductsResponse = null;
        } else {
          productsSessionStorageService.upsertProducts(searchResponse?.nbnProductsResponse, 'nbn', address);
        }
      }
      sessionStorage.setItem(SessionStorageKeys.address, JSON.stringify(address));
      bundleSearchService.saveProductsInStore(address, searchResponse);
      return Promise.resolve({ ...searchResponse, address });
    });
  }

  public loadProductsResponseFromStore(requestModel: CurrentSearchRequestModel): BundleSearchStore | null {
    const request: ProductSearchRequestModel = requestModel.productSearchRequestModel;

    // Check Products stored in the local Store
    const productResponseFromStorage = store.get(StoreKeys.bundleSearchStore);
    if (isSameAddress(productResponseFromStorage?.request?.productSearchRequestModel, request)) {
      return productResponseFromStorage;
    }

    // Check Products stored in session storage
    const productResponseFromSession = JSON.parse(sessionStorage.getItem(SessionStorageKeys.bundleProducts)) as BundleSearchStore;
    if (isSameAddress(productResponseFromSession?.request?.productSearchRequestModel, request)) {
      store.set(StoreKeys.bundleSearchStore, productResponseFromSession);
      return productResponseFromSession;
    }

    return null;
  }

  public saveProductsInStore(address: AddressDetailModel, bundleSearchResponse: BundleSearchResponse): void {
    const requestModel = this.getBundleRequestModel(address);
    const productStore: BundleSearchStore = new BundleSearchStore();

    // storing default tariff for elec
    let request = requestModel;
    if (bundleSearchResponse.energyProductsResponse) {
      request = this.setRequestModel(requestModel, bundleSearchResponse.energyProductsResponse);
      productStore.energyResponse = bundleSearchResponse.energyProductsResponse;
    }

    productStore.nbnResponse = bundleSearchResponse.nbnProductsResponse;
    productStore.mobileResponse = bundleSearchResponse.mobileProductsResponse;
    productStore.discounts = bundleSearchResponse.discounts;
    productStore.request = request;
    productStore.requestDateTime = new Date();
    productStore.address = address;
    store.set(StoreKeys.bundleSearchStore, productStore);

    const energyStore = new EnergySearchStore();
    energyStore.request = request;
    energyStore.response = productStore.energyResponse;

    const productSearchResponses: EnergySearchStore[] =
      JSON.parse(sessionStorage.getItem(SessionStorageKeys.productSearchResponse)) ?? ([] as EnergySearchStore[]);

    const targetAddressString = addressService.convertAddressToString(address);
    const addressIndex: number = productSearchResponses.length
      ? productSearchResponses.findIndex(
          (productSearchResponse: EnergySearchStore) => productSearchResponse.request.addressString === targetAddressString
        )
      : -1;

    if (addressIndex === -1) {
      productSearchResponses.push(energyStore);
    } else {
      productSearchResponses[addressIndex] = energyStore;
    }

    sessionStorage.setItem(SessionStorageKeys.productSearchResponse, JSON.stringify(productSearchResponses));
    sessionStorage.setItem(SessionStorageKeys.bundleProducts, JSON.stringify(productStore));
  }

  public setRequestModel(requestModel: CurrentSearchRequestModel, energyResponse: EnergyProductsResponse): CurrentSearchRequestModel {
    if (requestModel.selectedTariff.length === 0 && energyResponse.electricity && energyResponse.electricity.products.length > 0) {
      if (requestModel.searchRequestType === SearchRequestType.postcode || energyResponse.electricity.siteData.isDefaultRate) {
        requestModel.selectedTariff = 'Single Rate';
      } else {
        const productWithEstimate = energyResponse.electricity.products.find(
          (prod) => prod.priceEstimates && prod.priceEstimates.length > 0
        );
        if (productWithEstimate) {
          requestModel.selectedTariff = productWithEstimate.priceEstimates[0].tariffDescription;
        }
      }
    }
    return requestModel;
  }

  public getFirstAvailableProductCategory(
    bundleSearchResponse: BundleSearchResponse,
    isAuthenticated = false
  ): FirstAvailableProductCategory {
    if (
      bundleSearchResponse?.energyProductsResponse?.electricity?.products?.length > 0 &&
      !bundleSearchResponse?.energyProductsResponse?.electricity?.siteError &&
      !isAuthenticated
    ) {
      return 'energy';
    }
    if (
      bundleSearchResponse?.energyProductsResponse?.gas?.products?.length > 0 &&
      !bundleSearchResponse?.energyProductsResponse?.gas?.siteError &&
      !isAuthenticated
    ) {
      return 'energy';
    }

    if (bundleSearchResponse.nbnProductsResponse && bundleSearchResponse.nbnProductsResponse.products?.length > 0) {
      if (nbnProductService.IsNBNAvailable(bundleSearchResponse.nbnProductsResponse)) {
        return 'nbn';
      }
    }

    if (bundleSearchResponse.mobileProductsResponse && bundleSearchResponse.mobileProductsResponse.products?.length > 0) {
      return 'mobile plan';
    }

    return 'none';
  }
}

export const bundleSearchService = new BundleSearchService();
